import { formatDateTime, useLocale, useTranslation } from '@multaqa/i18n'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Skeleton } from '@multaqa/ui/components/ui/skeleton'
import { CopyIcon } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { InlineQueryError } from '@/components/inline-query-error'
import { localizedText } from '@/lib/localized-text'
import type { GroupRow } from '@/lib/platform'
import { useGroupCompanies, useGroupEntitlements } from './api'
import { ErasurePanel } from './erasure/erasure-panel'
import { isGranted, moduleSlug } from './group-detail-modules'
import { GroupCompanies, GroupSupportSessions } from './group-detail-sections'
import { GROUP_STATUSES } from './search'

export function OverviewTab({ group }: { group: GroupRow }) {
  return (
    <div className="space-y-6">
      <GroupFacts group={group} />
      <ErasurePanel group={group} />
      <CompaniesSection groupId={group.id} />
      <GroupSupportSessions groupId={group.id} />
    </div>
  )
}

function CompaniesSection({ groupId }: { groupId: string }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const companies = useGroupCompanies(groupId)

  if (companies.isLoading || companies.isError) {
    return (
      <section aria-labelledby="group-companies-heading">
        <h2 id="group-companies-heading" className="text-h3 mb-3">
          {t('groups.detail.companies.title', { count: 0 })}
        </h2>
        {companies.isLoading ? (
          <Skeleton className="h-24 w-full" data-testid="group-companies-loading" />
        ) : (
          <InlineQueryError
            title={t('error.asyncTitle')}
            description={t('error.asyncBody', {
              title: t('groups.detail.companies.title', { count: 0 }),
            })}
            retryLabel={t('action.retry')}
            onRetry={() => void companies.refetch()}
          />
        )}
      </section>
    )
  }
  return <GroupCompanies companies={companies.data ?? []} locale={locale} />
}

/**
 * The facts an operator reaches for first when they land on a group: its
 * identity (id, slug, when it was created), its commercial state (plan or
 * "modules only", presentation currency) and which modules are actually on —
 * the same effective-value read the entitlements tab's module cards use, so
 * the two never disagree about what "enabled" means.
 */
function GroupFacts({ group }: { group: GroupRow }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const entitlements = useGroupEntitlements(group.id)
  const [copied, setCopied] = useState(false)
  const known = (GROUP_STATUSES as readonly string[]).includes(group.status)
  const status = known ? group.status : 'active'
  const enabledModules = (entitlements.data ?? [])
    .filter((row) => row.key.startsWith('module.') && isGranted(row.effective_value))
    .map((row) => t(`module.names.${moduleSlug(row.key)}`))

  return (
    <section
      aria-labelledby="group-facts-heading"
      className="border border-border p-4"
      data-testid="group-facts-panel"
    >
      <h2 id="group-facts-heading" className="sr-only">
        {t('groups.detail.subtitle')}
      </h2>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3">
        <Fact label={t('groups.detail.overview.groupId')}>
          <button
            type="button"
            className="text-identifier flex items-center gap-1.5 font-mono tabular-nums hover:underline"
            onClick={() => {
              void navigator.clipboard.writeText(group.id)
              setCopied(true)
            }}
            data-testid="group-facts-id-copy"
          >
            {group.id}
            <CopyIcon className="size-3" aria-hidden="true" />
          </button>
          {copied ? (
            <span className="text-small text-muted-foreground">{t('action.copied')}</span>
          ) : null}
        </Fact>
        {group.slug ? (
          <Fact label={t('groups.detail.overview.slug')}>
            <span className="text-identifier font-mono">{group.slug}</span>
          </Fact>
        ) : null}
        {group.created_at ? (
          <Fact label={t('groups.detail.overview.created')}>
            <span className="text-body">{formatDateTime(new Date(group.created_at), locale)}</span>
          </Fact>
        ) : null}
        <Fact label={t('groups.columns.status')}>
          <Badge
            tone={status === 'active' ? 'success' : status === 'purged' ? 'danger' : 'warning'}
          >
            {t(`groups.status.${status}`)}
          </Badge>
        </Fact>
        <Fact label={t('groups.detail.overview.plan')}>
          <span className="text-body">
            {group.plan
              ? localizedText(group.plan.name, locale)
              : t('groups.detail.overview.modulesOnly')}
          </span>
        </Fact>
        <Fact label={t('groups.detail.overview.currency')}>
          <span className="text-body">{group.presentation_currency ?? '—'}</span>
        </Fact>
        <Fact label={t('groups.detail.overview.modules')} className="col-span-2 md:col-span-3">
          {enabledModules.length === 0 ? (
            <span className="text-small text-muted-foreground">
              {t('groups.detail.overview.noModules')}
            </span>
          ) : (
            <span className="text-body" data-testid="group-facts-modules">
              {enabledModules.join(', ')}
            </span>
          )}
        </Fact>
      </dl>
    </section>
  )
}

function Fact({
  label,
  children,
  className,
}: {
  label: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <dt className="text-small text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  )
}
